import { Link } from "react-router-dom";
import { Mail, Truck, Wrench, Banknote, RotateCcw, ArrowRight, MapPin, Clock } from "lucide-react";
import { PlankSymbol, Lockup } from "./Logo";
import { ShareBar } from "./ShareBar";
import { Reveal } from "./Motion";
import { POLICIES, COMPANY } from "../data/policies";

const COLS = [
  ["Shop", [["/product/pustak-rack", "Pustak Rack"], ["/product/pustak-rack?finish=oak", "Pustak Rack in Oak"], ["/product/pustak-rack?finish=walnut", "Pustak Rack in Walnut"], ["/shop/modules", "Add-on modules"], ["/shop", "All furniture"], ["/wishlist", "Wishlist"]]],
  ["Learn", [["/blog", "Blog"], ["/#how-it-builds", "How it builds"], ["/#faq", "FAQs"], ["/story", "Our story"]]],
  ["Policies", POLICIES.map((p) => [`/policies/${p.slug}`, p.title])],
];

const USPS = [[Truck, "24 Hrs free delivery"], [Wrench, "No carpenter needed"], [Banknote, "Pay on delivery"], [RotateCcw, "10-day returns"]];

const Heading = ({ children }) => (
  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brass">
    <span className="h-px w-4 bg-brass" />{children}
  </p>
);

const CtaBand = () => (
  <div className="relative border-b border-linen/10">
    <Reveal className="container-hw grid gap-8 py-16 sm:py-20 lg:grid-cols-12 lg:items-end">
      <div className="lg:col-span-7">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brass">H&amp;W Furniture · Hyderabad</p>
        <p className="t-display mt-4 text-linen">Build it <span className="text-brass">together.</span></p>
        <p className="t-lead mt-4 max-w-md text-linen/70">DIY modular furniture you put together in one evening, and grow one module at a time.</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
        <Link to="/product/pustak-rack" data-testid="footer-cta-shop" className="btn-sheesham px-7 py-3.5">Shop the Pustak Rack <ArrowRight className="h-4 w-4" /></Link>
        <Link to="/blog" data-testid="footer-cta-blog" className="inline-flex items-center justify-center rounded-lg border border-linen/50 px-7 py-3.5 text-sm font-medium transition-[background-color,border-color] hover:border-linen hover:bg-linen/10">Read the blog</Link>
      </div>
    </Reveal>
    <ul className="container-hw grid grid-cols-2 gap-3 pb-12 sm:grid-cols-4">
      {USPS.map(([I, t]) => (
        <li key={t} className="flex items-center gap-3 rounded-xl border border-linen/10 px-4 py-3 text-sm text-linen/85"><span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brass/15 text-brass"><I className="h-4 w-4" /></span>{t}</li>
      ))}
    </ul>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="grain relative mt-16 overflow-hidden rounded-t-[2rem] bg-walnut text-linen border-t border-brass sm:rounded-t-[2.5rem]">
    <PlankSymbol bar="currentColor" className="pointer-events-none absolute -right-24 top-24 hidden h-[520px] w-[520px] text-linen/[0.035] lg:block" />
    <CtaBand />
    <div className="container-hw relative grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-12">
      <div className="sm:col-span-2 lg:col-span-4">
        <Link to="/" data-testid="footer-logo-link" aria-label="H&W Furniture home" className="inline-flex p-1"><Lockup reverse className="h-12" /></Link>
        <p className="t-body mt-6 max-w-sm text-linen/70">
          H&amp;W stands for Husband &amp; Wife. We make do-it-yourself modular furniture for Hyderabad homes: flat-packed, built at home, made to grow.
        </p>
        <div className="mt-6 space-y-2 text-sm text-linen/70">
          <a href="mailto:care@hwfurniture.in" data-testid="footer-email" className="flex items-center gap-2 transition-colors hover:text-linen"><Mail className="h-4 w-4 text-brass" />care@hwfurniture.in</a>
          <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-brass" />Mon to Sat, 10am to 7pm</p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brass" />Hyderabad, Telangana</p>
        </div>
        <div className="mt-7 text-linen [&_a]:border-linen/20 [&_a]:bg-transparent [&_button]:border-linen/20 [&_button]:bg-transparent"><ShareBar path="/" title="H&W Furniture" text="DIY modular furniture for Hyderabad homes from H&W Furniture" testid="footer-share" /></div>
      </div>
      {COLS.map(([title, links]) => (
        <nav key={title} aria-label={title} className={title === "Policies" ? "lg:col-span-3" : "lg:col-span-2 lg:col-start-auto"}>
          <Heading>{title}</Heading>
          <ul className="mt-5 space-y-3 text-sm">
            {links.map(([to, label]) => (
              <li key={label}><Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-linen/75 transition-colors hover:text-brass">{label}</Link></li>
            ))}
          </ul>
        </nav>
      ))}
    </div>
    <div className="relative border-t border-linen/10">
      <div className="container-hw flex flex-col gap-3 py-6 text-xs text-linen/70 md:flex-row md:items-center md:justify-between">
        <p data-testid="footer-legal-name">© {new Date().getFullYear()} {COMPANY}. All rights reserved.</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/policies/terms" className="hover:text-linen">Terms</Link>
          <Link to="/policies/privacy" className="hover:text-linen">Privacy</Link>
          <Link to="/policies/returns" className="hover:text-linen">Returns</Link>
          <span data-testid="footer-made-in-india">Made with ❤️ in India</span>
        </div>
      </div>
    </div>
  </footer>
);
